import { useMemo } from 'react'

import { match } from 'ts-pattern'

import {
  ALL_BILLING_FREQUENCIES,
  type SelectableBillingFrequency
} from '@sequencehq/utils'

// Types only, so this never loops back into the helpers module at runtime —
// `isOperatorViable` there calls into this file.
import type {
  IsSubOperator,
  PriceAttributeField,
  SameProductSibling
} from './ProductPropertyEditor.helpers'
import {
  IS_ONLY_SUB_OPERATOR_OPTIONS,
  BOOLEAN_SUB_OPERATOR_OPTIONS
} from './product.constants'

/**
 * Whether another billing-frequency row can be added for this product.
 *
 * An "is" row settles the product's frequency outright, so nothing can be
 * asserted alongside it. "Is not" rows each rule out one more frequency, so
 * they combine — until every frequency is spoken for and a further row would
 * have nothing left to pick.
 *
 *   is monthly               -> no more rows for this product
 *   is not monthly           -> more "is not" rows, never an "is"
 *   is not <every frequency> -> no more rows
 */
export const billingFrequencyRowAvailable = (
  sameProductSiblings: SameProductSibling[]
): boolean => {
  const siblings = billingFrequencySiblings(sameProductSiblings)
  if (siblings.some(s => s.subOperator === 'IS')) return false
  return (
    usedBillingFrequencies(siblings).length < ALL_BILLING_FREQUENCIES.length
  )
}

/**
 * Narrows a billing-frequency row's two dropdowns to the choices its siblings
 * leave open (see `billingFrequencyRowAvailable` for the rule). The row's own
 * current pick always stays offered, so an existing row never renders a blank
 * select.
 *
 * PRICING_MODEL only ever gets one row per product, so it has no sibling to
 * exhaust and both lists pass through untouched. BILLING_TYPE is the same,
 * except it offers "is" on its own.
 */
export const usePriceAttributeOptions = (params: {
  field: PriceAttributeField
  subOperator: IsSubOperator | undefined
  attribute: string | undefined
  options: Array<{ label: string; value: string }>
  sameProductSiblings: SameProductSibling[]
}): {
  subOperatorOptions: Array<{ label: string; value: IsSubOperator }>
  attributeOptions: Array<{ label: string; value: string }>
} => {
  const { field, subOperator, attribute, options, sameProductSiblings } = params

  return useMemo(() => {
    if (field === 'billingType') {
      return {
        subOperatorOptions: IS_ONLY_SUB_OPERATOR_OPTIONS,
        attributeOptions: options
      }
    }

    if (field !== 'billingFrequency') {
      return {
        subOperatorOptions: BOOLEAN_SUB_OPERATOR_OPTIONS,
        attributeOptions: options
      }
    }

    const siblings = billingFrequencySiblings(sameProductSiblings)
    const hasIsSibling = siblings.some(s => s.subOperator === 'IS')
    const hasCommittedSibling = siblings.some(s => Boolean(s.subOperator))

    const subOperatorOptions = BOOLEAN_SUB_OPERATOR_OPTIONS.filter(option => {
      // The row's own pick always stays, so an existing row never blanks out.
      if (option.value === subOperator) return true
      return (
        match(option.value)
          // A sibling that has settled on either comparison rules out "is" —
          // the frequency is already fixed, or being narrowed down.
          .with('IS', () => !hasCommittedSibling)
          .with('IS_NOT', () => !hasIsSibling)
          .exhaustive()
      )
    })

    // Only "is not" rows stack, so only they can duplicate each other.
    if (subOperator !== 'IS_NOT') {
      return { subOperatorOptions, attributeOptions: options }
    }
    const used = usedBillingFrequencies(siblings)
    return {
      subOperatorOptions,
      attributeOptions: options.filter(
        option =>
          option.value === attribute ||
          !used.includes(option.value as SelectableBillingFrequency)
      )
    }
  }, [field, subOperator, attribute, options, sameProductSiblings])
}

const billingFrequencySiblings = (
  sameProductSiblings: SameProductSibling[]
): SameProductSibling[] =>
  sameProductSiblings.filter(s => s.operator === 'BILLING_FREQUENCY')

// The frequencies sibling "is not" rows have already ruled out. A row that
// hasn't picked one yet contributes nothing.
const usedBillingFrequencies = (
  siblings: SameProductSibling[]
): SelectableBillingFrequency[] =>
  siblings.reduce<SelectableBillingFrequency[]>((acc, sibling) => {
    if (sibling.subOperator !== 'IS_NOT') return acc
    if (!sibling.billingFrequency) return acc
    return [...acc, sibling.billingFrequency]
  }, [])

/**
 * The sub-operator a fresh billing-frequency row should start on. "Is" is only
 * a valid start when no sibling has committed to a comparison yet — otherwise
 * the seeded default would be a choice the dropdown no longer offers.
 */
export const defaultBillingFrequencySubOperator = (
  sameProductSiblings: SameProductSibling[]
): IsSubOperator =>
  billingFrequencySiblings(sameProductSiblings).some(s =>
    Boolean(s.subOperator)
  )
    ? 'IS_NOT'
    : 'IS'
